import useSWRImmutable from 'swr/immutable'
import useSWRMutation from 'swr/mutation'
import { mutate } from 'swr'

import { getEvent, getEvents, rsvpEvent } from '../../../api/events'
import { getCompanyEvents } from '../../../api/companies'
import type { Event, EventFilters } from '../../../api/events/types'

// Constants for cache keys
const CACHE_BASE = 'events'
const CACHE_KEYS = {
  ALL_EVENTS: `${CACHE_BASE}/all`,
  EVENTS_BY_ID: (eventId: number) => `${CACHE_BASE}/${eventId}`,
  COMPANY_EVENTS: (companyId: number) => `${CACHE_BASE}/company/${companyId}`,
} as const

// Hook for all events
export const useEvents = (filters?: EventFilters, limit?: number) => {
  return useSWRImmutable(
    [CACHE_KEYS.ALL_EVENTS, filters, limit],
    () => getEvents({ params: filters, limit })
  )
}

// Hook for a single event
export const useEvent = (eventId: number) => {
  return useSWRImmutable(
    eventId ? CACHE_KEYS.EVENTS_BY_ID(eventId) : null,
    () => getEvent(eventId)
  )
}

// Hook for company events
export const useCompanyEvents = (companyId: number) => {
  return useSWRImmutable(
    companyId ? CACHE_KEYS.COMPANY_EVENTS(companyId) : null,
    () => getCompanyEvents(companyId)
  )
}

// Hook for RSVP mutation
export const useEventRsvp = (eventId: number) => {
  const { trigger, isMutating, error } = useSWRMutation(
    CACHE_KEYS.EVENTS_BY_ID(eventId),
    rsvpEvent,
    {
      onSuccess: async (data: Partial<Event>) => {
        const { rsvpStatus } = data

        // Update single event cache
        await mutate(
          CACHE_KEYS.EVENTS_BY_ID(eventId),
          (event: Partial<Event> | undefined) => event ? { ...event, rsvpStatus } : {},
          { revalidate: false }
        )

        // invalidate rest of events cache
        await mutate(
          (key) => Array.isArray(key) && key[0] === CACHE_KEYS.ALL_EVENTS,
          undefined,
          { revalidate: true }
        )
      }
    }
  )

  return {
    trigger,
    isMutating,
    error
  }
}
